import React, { useState, useRef, useEffect } from "react";
import { X, Trash2, PlusCircle, Share2, Clock, CheckCircle, XCircle } from "lucide-react";
import { useRemoteRegistry, type LocalApp, type OrgApp, type SubmissionStatus } from "../registry/RemoteRegistryContext";
import { useAuth } from "../auth/AuthContext";
import { iconRegistry } from "./iconRegistry";
import type { RemoteConfig } from "../types";

const ICON_OPTIONS = Object.keys(iconRegistry);

function IconPicker({ value, onChange, compact }: { value: string; onChange: (v: string) => void; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const SelectedIcon = iconRegistry[value] ?? iconRegistry["LayoutDashboard"];

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  if (compact) {
    return (
      <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title="Change icon"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 26, height: 26, padding: 0,
            border: "1px solid #ddd", borderRadius: 6,
            background: "#fff", cursor: "pointer", color: "#5f6368",
          }}
        >
          <SelectedIcon size={15} />
        </button>

        {open && (
          <div style={{
            position: "absolute", top: "100%", left: 0, zIndex: 50,
            background: "#fff", border: "1px solid #ddd", borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 180, width: 168,
            overflowY: "auto", marginTop: 2, display: "flex", flexWrap: "wrap", padding: 4,
          }}>
            {ICON_OPTIONS.map((key) => {
              const Icon = iconRegistry[key];
              return (
                <button
                  key={key}
                  type="button"
                  title={key}
                  onClick={() => { onChange(key); setOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 28, height: 28, padding: 0, margin: 1,
                    background: key === value ? "#e6f3ed" : "none",
                    border: "none", borderRadius: 4, cursor: "pointer",
                    color: key === value ? "#136945" : "#333",
                  }}
                >
                  <Icon size={15} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          width: "100%", padding: "8px 10px",
          border: "1px solid #ddd", borderRadius: 6,
          background: "#fff", cursor: "pointer", fontSize: 14,
          textAlign: "left" as const,
        }}
      >
        <SelectedIcon size={16} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1 }}>{value}</span>
        <span style={{ color: "#aaa", fontSize: 12 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          background: "#fff", border: "1px solid #ddd", borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 220,
          overflowY: "auto", marginTop: 2,
        }}>
          {ICON_OPTIONS.map((key) => {
            const Icon = iconRegistry[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => { onChange(key); setOpen(false); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  width: "100%", padding: "7px 10px",
                  background: key === value ? "#e6f3ed" : "none",
                  border: "none", cursor: "pointer", fontSize: 13,
                  textAlign: "left" as const,
                  color: key === value ? "#136945" : "#333",
                  fontWeight: key === value ? 600 : 400,
                }}
              >
                <Icon size={15} style={{ flexShrink: 0 }} />
                {key}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface FormState {
  label: string;
  url: string;
  type: "iframe" | "mfe";
  icon: string;
  scope: string;
  module: string;
}

const EMPTY_FORM: FormState = {
  label: "",
  url: "",
  type: "iframe",
  icon: "LayoutDashboard",
  scope: "",
  module: "",
};

interface Props {
  onClose: () => void;
}

const STATUS_ICON: Record<SubmissionStatus, React.ReactNode> = {
  local: null,
  pending: <Clock size={13} style={{ color: "#f59e0b" }} />,
  approved: <CheckCircle size={13} style={{ color: "#16a34a" }} />,
  rejected: <XCircle size={13} style={{ color: "#dc2626" }} />,
};

const STATUS_LABEL: Record<SubmissionStatus, string> = {
  local: "",
  pending: "pending",
  approved: "approved",
  rejected: "rejected",
};

export function ManageAppsModal({ onClose }: Props) {
  const { builtInRemotes, orgApps, userApps, isAdmin, addApp, updateAppIcon, updateOrgAppIcon, removeApp, submitForApproval } = useRemoteRegistry();
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function validate(): boolean {
    const errs: Partial<FormState> = {};
    if (!form.label.trim()) errs.label = "Required";
    if (!form.url.trim()) errs.url = "Required";
    else { try { new URL(form.url); } catch { errs.url = "Must be a valid URL"; } }
    if (form.type === "mfe") {
      if (!form.scope.trim()) errs.scope = "Required for MFE";
      if (!form.module.trim()) errs.module = "Required for MFE";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    const app: Omit<RemoteConfig, "name" | "route"> = {
      label: form.label.trim(), url: form.url.trim(), type: form.type, icon: form.icon,
      ...(form.type === "mfe" && { scope: form.scope.trim(), module: form.module.trim() }),
    };
    addApp(app);
    setForm(EMPTY_FORM);
    setShowForm(false);
  }

  async function handleShare(name: string) {
    setSubmitting(name);
    setSubmitError(null);
    try {
      await submitForApproval(name);
    } catch (err: any) {
      setSubmitError(err.message ?? "Submission failed");
    } finally {
      setSubmitting(null);
    }
  }

  function field(label: string, key: keyof FormState, type = "text") {
    return (
      <div style={styles.field}>
        <label style={styles.label}>{label}</label>
        <input
          style={{ ...styles.input, ...(errors[key] ? styles.inputError : {}) }}
          type={type} value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          placeholder={key === "url" ? "https://..." : ""}
        />
        {errors[key] && <span style={styles.errorText}>{errors[key]}</span>}
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Manage Apps</h2>
          <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>

        {/* Built-in apps */}
        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>Built-in</h3>
          {builtInRemotes.map((app) => <AppRow key={app.name} app={app} />)}
        </section>

        {/* Org-approved apps */}
        {orgApps.length > 0 && (
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>Org-approved</h3>
            {orgApps.map((app) => {
              const canEditIcon = isAdmin || app.submitted_by?.toLowerCase() === user.username.toLowerCase();
              return (
                <AppRow
                  key={app.name} app={app} badge="org"
                  onIconChange={canEditIcon ? (icon) => { updateOrgAppIcon(app.id, icon).catch(() => {}); } : undefined}
                />
              );
            })}
          </section>
        )}

        {/* User apps */}
        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>Your Apps</h3>
          {submitError && <p style={{ color: "#dc2626", fontSize: 13, marginBottom: 8 }}>{submitError}</p>}
          {userApps.length === 0 && <p style={styles.empty}>No apps added yet.</p>}
          {userApps.map((app) => (
            <AppRow
              key={app.name} app={app}
              onRemove={() => removeApp(app.name)}
              onIconChange={(icon) => updateAppIcon(app.name, icon)}
              onShare={app.submissionStatus === "local" || !app.submissionStatus
                ? () => handleShare(app.name) : undefined}
              sharing={submitting === app.name}
              status={app.submissionStatus ?? "local"}
            />
          ))}
        </section>

        {/* Add App form */}
        <section style={styles.section}>
          {!showForm ? (
            <button style={styles.addBtn} onClick={() => setShowForm(true)}>
              <PlusCircle size={16} style={{ marginRight: 6 }} /> Add new app
            </button>
          ) : (
            <form onSubmit={handleSubmit}>
              <h3 style={styles.sectionTitle}>Add New App</h3>
              {field("Label", "label")}
              {field("URL", "url")}
              <div style={styles.field}>
                <label style={styles.label}>Type</label>
                <select style={styles.input} value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as "iframe" | "mfe" }))}>
                  <option value="iframe">iframe</option>
                  <option value="mfe">MFE (Module Federation)</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Icon</label>
                <IconPicker value={form.icon} onChange={(v) => setForm((f) => ({ ...f, icon: v }))} />
              </div>
              {form.type === "mfe" && <>{field("Scope", "scope")}{field("Module", "module")}</>}
              <div style={styles.formActions}>
                <button type="button" style={styles.cancelBtn}
                  onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>Cancel</button>
                <button type="submit" style={styles.submitBtn}>Add App</button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

function AppRow({ app, onRemove, onIconChange, onShare, sharing, status, badge }: {
  app: LocalApp | RemoteConfig;
  onRemove?: () => void;
  onIconChange?: (icon: string) => void;
  onShare?: () => void;
  sharing?: boolean;
  status?: SubmissionStatus;
  badge?: "org";
}) {
  const Icon = iconRegistry[app.icon] ?? iconRegistry["LayoutDashboard"];
  return (
    <div style={styles.appRow}>
      {onIconChange ? (
        <IconPicker value={app.icon} onChange={onIconChange} compact />
      ) : (
        <Icon size={18} style={{ flexShrink: 0, color: "#5f6368" }} />
      )}
      <span style={styles.appLabel}>{app.label}</span>
      <span style={styles.appUrl}>{app.url}</span>
      {status && status !== "local" && (
        <span style={{ ...styles.statusBadge, display: "flex", alignItems: "center", gap: 3 }}>
          {STATUS_ICON[status]}{STATUS_LABEL[status]}
        </span>
      )}
      {badge === "org" && <span style={styles.builtInBadge}>org</span>}
      {!onRemove && !badge && !status && <span style={styles.builtInBadge}>built-in</span>}
      {onShare && (
        <button style={styles.shareBtn} onClick={onShare} disabled={sharing} title="Share with everyone">
          <Share2 size={14} />
        </button>
      )}
      {onRemove && (
        <button style={styles.removeBtn} onClick={onRemove} title="Remove">
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 8, width: 560, maxWidth: "90vw", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 4px 24px rgba(0,0,0,0.18)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e0e0e0" },
  title: { fontSize: 18, fontWeight: 600, margin: 0 },
  closeBtn: { background: "none", border: "none", cursor: "pointer", color: "#5f6368", display: "flex", alignItems: "center", padding: 4 },
  section: { padding: "16px 20px", borderBottom: "1px solid #f0f0f0" },
  sectionTitle: { fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase" as const, marginBottom: 10 },
  empty: { fontSize: 14, color: "#aaa", fontStyle: "italic" },
  appRow: { display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #f5f5f5" },
  appLabel: { fontWeight: 500, fontSize: 14, minWidth: 110 },
  appUrl: { fontSize: 12, color: "#888", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const },
  removeBtn: { background: "none", border: "none", cursor: "pointer", color: "#d93025", display: "flex", alignItems: "center", padding: 4, flexShrink: 0 },
  shareBtn: { background: "none", border: "none", cursor: "pointer", color: "#136945", display: "flex", alignItems: "center", padding: 4, flexShrink: 0 },
  builtInBadge: { fontSize: 11, color: "#888", background: "#f0f0f0", borderRadius: 4, padding: "2px 6px", flexShrink: 0 },
  statusBadge: { fontSize: 11, color: "#666", background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 4, padding: "2px 6px", flexShrink: 0 },
  addBtn: { display: "flex", alignItems: "center", background: "none", border: "1px dashed #bbb", borderRadius: 6, cursor: "pointer", padding: "8px 14px", fontSize: 14, color: "#444", width: "100%" },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4, color: "#444" },
  input: { width: "100%", padding: "8px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 14, boxSizing: "border-box" as const },
  inputError: { borderColor: "#d93025" },
  errorText: { fontSize: 12, color: "#d93025", marginTop: 4, display: "block" },
  formActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 },
  cancelBtn: { background: "none", border: "1px solid #ddd", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 14 },
  submitBtn: { background: "#136945", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 14, fontWeight: 500 },
};

